import React from 'react'
import { motion } from 'framer-motion'
import { Users, Mail, Linkedin } from 'lucide-react'
import { Section, Card, Button } from '../components/ui'
import { cn } from '../utils'

const teamGroups = [
  {
    name: 'Research Team',
    description: 'Our researchers bring together expertise in data science, economics, and development studies.',
    members: [
      {
        name: 'Research Director',
        role: 'Data Science & AI',
        bio: 'Leading cutting-edge research in machine learning and data analytics for African contexts.',
      },
      {
        name: 'Senior Researcher',
        role: 'Development Economics',
        bio: 'Analyzing economic trends and policy impacts across African markets.',
      },
      {
        name: 'Research Analyst',
        role: 'Quantitative Analysis',
        bio: 'Applying statistical methods to uncover insights from complex datasets.',
      },
    ],
  },
  {
    name: 'Innovation Team',
    description: 'Building practical solutions that bridge research and real-world impact.',
    members: [
      {
        name: 'Innovation Lead',
        role: 'Digital Transformation',
        bio: 'Designing technology solutions for sustainable development challenges.',
      },
      {
        name: 'Product Designer',
        role: 'User Experience',
        bio: 'Creating human-centered designs that work across diverse contexts.',
      },
    ],
  },
  {
    name: 'Advisory Board',
    description: 'Distinguished experts guiding our strategic direction.',
    members: [
      {
        name: 'Strategic Advisor',
        role: 'Policy & Governance',
        bio: 'Former government official with deep experience in development policy.',
      },
      {
        name: 'Technical Advisor',
        role: 'Technology & Innovation',
        bio: 'Serial entrepreneur and technology leader in the African tech ecosystem.',
      },
    ],
  },
]

const Team: React.FC = () => {
  return (
    <>
      {/* Hero */}
      <Section background="muted" padding="xl" className="pt-32 md:pt-40">
        <motion.div
          className="max-w-3xl mx-auto text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-sm font-medium text-primary-500 dark:text-primary-400 uppercase tracking-wider mb-4">
            Our Team
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 dark:text-white mb-6">
            Passionate About Africa's Future
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
            Our team combines deep expertise with a shared commitment to driving
            positive change across Africa.
          </p>
        </motion.div>
      </Section>

      {/* Team Groups */}
      {teamGroups.map((group, groupIndex) => (
        <Section
          key={group.name}
          background={groupIndex % 2 === 0 ? 'default' : 'muted'}
          padding="lg"
        >
          <motion.div
            className="mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3">
              {group.name}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl">
              {group.description}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {group.members.map((member, memberIndex) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: memberIndex * 0.1 }}
              >
                <Card variant="default" padding="lg" className="h-full">
                  {/* Avatar placeholder */}
                  <div className={cn(
                    'w-16 h-16 rounded-full mb-4',
                    'bg-gradient-to-br from-primary-100 to-primary-200',
                    'dark:from-primary-900/50 dark:to-primary-800/50',
                    'flex items-center justify-center'
                  )}>
                    <Users className="w-8 h-8 text-primary-500 dark:text-primary-400" />
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                    {member.name}
                  </h3>
                  <p className="text-sm text-primary-500 dark:text-primary-400 mb-3">
                    {member.role}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    {member.bio}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </Section>
      ))}

      {/* Join the Team CTA */}
      <Section background="default" padding="lg">
        <motion.div
          className="max-w-2xl mx-auto text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">
            Join Our Team
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-8">
            We're always looking for talented individuals who share our passion
            for Africa's development. If you're interested in contributing to
            our mission, we'd love to hear from you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              as="a"
              href="mailto:careers@matno.org"
              variant="primary"
              icon={<Mail className="w-4 h-4" />}
            >
              Contact Us
            </Button>
            <Button
              as="a"
              href="https://linkedin.com"
              target="_blank"
              variant="outline"
              icon={<Linkedin className="w-4 h-4" />}
            >
              Follow on LinkedIn
            </Button>
          </div>
        </motion.div>
      </Section>
    </>
  )
}

export default Team
